import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/shared/lib/api'
import { login as loginToStore } from '@/shared/store/auth.store'
import { useTranslation } from 'react-i18next'
import { Copy } from 'lucide-react'
import { initializeTokenService } from '@/shared/lib/tokenService'
import { AuthLayout } from '@workspace/ui/components/AuthLayout'
import { AuthErrorState } from '@workspace/ui/components/AuthErrorState'
import { AuthLoadingState } from '@workspace/ui/components/AuthLoadingState'
import { AuthPendingApprovalState } from '@workspace/ui/components/AuthPendingApprovalState'

export const Route = createFileRoute('/auth/callback')({
    component: AzureCallbackPage,
})

function AzureCallbackPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [copySuccess, setCopySuccess] = useState(false)
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const sessionState = params.get('session_state') || ''
    const oauthError = params.get('error')
    const rawErrorDescription = params.get('error_description') || ''
    const rawErrorUri = params.get('error_uri') || ''
    const errorUri = rawErrorUri.startsWith('http://') || rawErrorUri.startsWith('https://') ? rawErrorUri : ''
    const errorDescription = useMemo(() => {
        if (!rawErrorDescription) return ''
        try {
            return decodeURIComponent(rawErrorDescription.replace(/\+/g, ' '))
        } catch (e) {
            return rawErrorDescription
        }
    }, [rawErrorDescription])

    const isPendingApproval = (error: any): boolean => {
        if (!error) return false

        const status = error.response?.status || error.status || error.response?.data?.statusCode
        const message = error.response?.data?.message || error.message || ''
        const errorCode = error.response?.data?.code || error.code || ''

        // Check if user is blocked
        if (
            message === 'error.userBlocked' ||
            message.includes('userBlocked') ||
            message.includes('blocked') ||
            message.includes('BLOCKED') ||
            errorCode === 'USER_BLOCKED' ||
            errorCode === 'BLOCKED'
        ) {
            return true
        }

        if (status === 403) {
            return (
                message.includes('PENDING_APPROVAL') ||
                message.includes('WAITING_APPROVAL') ||
                message.includes('chờ duyệt') ||
                message.includes('pending') ||
                message.includes('chưa được cấp quyền') ||
                message.includes('not granted access') ||
                message.includes('chưa được cấp quyền') ||
                message.includes('not granted access') ||
                errorCode === 'PENDING_APPROVAL' ||
                errorCode === 'WAITING_APPROVAL'
            )
        }
        if (message === 'error.userInactive' || message.includes('userInactive')) {
            return true
        }

        return false
    }

    const isBlockedError = (err: any): boolean => {
        if (!err) return false
        const message = err.response?.data?.message || err.message || ''
        const errorCode = err.response?.data?.code || err.code || ''

        return (
            message === 'error.userBlocked' ||
            message.includes('userBlocked') ||
            message.includes('blocked') ||
            message.includes('BLOCKED') ||
            errorCode === 'USER_BLOCKED' ||
            errorCode === 'BLOCKED'
        )
    }

    const isUserPendingApproval = (userData: any): boolean => {
        if (!userData) return false

        return userData.status !== 'ACTIVE'
    }

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['azure-callback', code],
        queryFn: () => {
            if (!code) {
                throw new Error('No authorization code provided')
            }
            return api.auth.azureCallback(code, sessionState)
        },
        enabled: !!code && !oauthError,
        retry: false,
    })

    const pendingApproval = isPendingApproval(error) || (data && isUserPendingApproval(data.user))

    useEffect(() => {
        if (pendingApproval) {
            const userParam = isBlockedError(error) ? 'Blocked' : 'Inactive'
            navigate({ to: `/login?user=${userParam}` })
        }
    }, [pendingApproval, error, navigate])

    useEffect(() => {
        if (data && !isUserPendingApproval(data.user)) {
            loginToStore(data.user, data.token.accessToken, data.refreshToken.refreshToken, data.token.expiresIn)

            initializeTokenService()

            navigate({ to: '/dashboard' })
        }
    }, [data, navigate])

    useEffect(() => {
        if (error && !oauthError && !pendingApproval) {
            console.error('Azure login failed:', error)
            const timer = setTimeout(() => {
                navigate({ to: '/login' })
            }, 3000)
            return () => clearTimeout(timer)
        }
    }, [error, navigate, oauthError, pendingApproval])
    const handleCopyError = async () => {
        const errorText = `Error: ${oauthError || 'Unknown'}
Description: ${errorDescription || 'No description'}
${errorUri ? `More Info: ${decodeURIComponent(errorUri)}` : ''}`

        try {
            await navigator.clipboard.writeText(errorText)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
        } catch (err) {
            console.error('Failed to copy:', err)
        }
    }
    const handleRetry = () => {
        const userParam = isBlockedError(error) ? 'Blocked' : 'Inactive'
        navigate({ to: `/login?user=${userParam}` })
    }

    return (
        <AuthLayout>
            {/* Pending Approval Screen */}
            {pendingApproval ? (
                <AuthPendingApprovalState
                    title={t('login.noPermissionTitle')}
                    message1={t('login.noPermissionMessage1')}
                    message2={t('login.noPermissionMessage2')}
                    onRetry={handleRetry}
                    retryButtonLabel={t('login.backButton')}
                />
            ) : oauthError ? (
                <AuthErrorState
                    title={t('authCallback.errorHeading')}
                    message={oauthError}
                    description={errorDescription}
                    onRetry={handleRetry}
                    retryButtonLabel={t('authCallback.actions.goToLogin')}
                    copyButton={{
                        onClick: handleCopyError,
                        label: copySuccess ? t('authCallback.errorDetailsCopied') : t('authCallback.copyErrorDetails'),
                        isLoading: copySuccess,
                    }}
                    errorUri={errorUri ? decodeURIComponent(errorUri) : undefined}
                    errorUriLabel={t('authCallback.errorUriLabel')}
                />
            ) : (
                /* Loading or Success State */
                <>
                    {isLoading && <AuthLoadingState message={t('authCallback.processing')} type="loading" />}
                    {error && (
                        <>
                            <AuthLoadingState message={t('login.validation.loginFailed')} type="error" />
                            <p className="text-xs text-muted-foreground text-center">{t('login.redirectingToLogin')}</p>
                        </>
                    )}
                    {data && !pendingApproval && (
                        <AuthLoadingState message={t('authCallback.successMessage')} type="success" />
                    )}
                </>
            )}
        </AuthLayout>
    )
}
